import { useMemo } from "react";
import { useFetchActions } from "./useFetchActions";
import { useUpdateActionStatus } from "./useUpdateActionStatus";
import {
  ACTION_STATUS,
  TARGET_OBJECT_TYPE,
  type ActionTypeSlug,
} from "../actions.constants";
import { usePlatformStore } from "@platform/store";

type UseCompleteOrgActionArgs = {
  actionTypeSlug: ActionTypeSlug;
  organizationId: number;
  programId?: number;
};

type UseCompleteOrgActionResult = {
  /** Call this to mark the action as completed */
  complete: () => void;
  /** Whether the action exists and can be completed */
  canComplete: boolean;
  /** The action ID if found */
  actionId: number | undefined;
};

/**
 * Hook to find and complete an organization-related action.
 * Finds an action by type slug and organization ID, returns a function to mark it completed.
 *
 * @example
 * ```typescript
 * const { complete } = useCompleteOrgAction({
 *   actionTypeSlug: ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO,
 *   organizationId: 123,
 * });
 *
 * // In success handler:
 * complete();
 * ```
 */
export function useCompleteOrgAction({
  actionTypeSlug,
  organizationId,
  programId,
}: UseCompleteOrgActionArgs): UseCompleteOrgActionResult {
  const user = usePlatformStore((state) => state.user);

  const { data: actionsData } = useFetchActions({
    programId,
    user,
    isEnabled: !!user,
  });

  const action = useMemo(() => {
    const matches = actionsData?.actions?.filter(
      (a) =>
        a.action_type?.slug === actionTypeSlug &&
        a.target_object_type === TARGET_OBJECT_TYPE.ORGANIZATION &&
        (a.target_object_data as Record<string, unknown>)?.id === organizationId
    );
    if (!matches?.length) return undefined;
    // Prefer a non-completed action; fall back to first match
    return (
      matches.find((a) => a.current_status?.name !== ACTION_STATUS.COMPLETED) ??
      matches[0]
    );
  }, [actionsData?.actions, actionTypeSlug, organizationId]);

  const { mutate: updateStatus } = useUpdateActionStatus({
    actionId: action?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
  });

  const complete = () => {
    if (action) {
      updateStatus();
    }
  };

  return {
    complete,
    canComplete: !!action,
    actionId: action?.id,
  };
}
